import { useState, useEffect, useCallback } from 'react';
import { estimateAPI } from '../../services/api';

/**
 * TruckingParameters — CRUD table for managing trucking cost parameters.
 * Fetches all parameters on mount and provides inline editing per row.
 */
const TruckingParameters = () => {
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchParams = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await estimateAPI.getTruckingParameters();
      setParams(result.data || []);
    } catch (err) {
      setError('Failed to load trucking parameters');
      console.error('TruckingParameters fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParams();
  }, [fetchParams]);

  const handleEdit = (param) => {
    setEditingId(param.id);
    setEditForm({
      region: param.region || '',
      direction: param.direction || '',
      costPerKm: param.costPerKm ?? '',
      basePrice: param.basePrice ?? '',
      roadQualityFactor: param.roadQualityFactor ?? '',
      notes: param.notes || ''
    });
    setFeedback(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback(null);

      // Convert numeric fields
      const data = {
        ...editForm,
        costPerKm: editForm.costPerKm !== '' ? Number(editForm.costPerKm) : undefined,
        basePrice: editForm.basePrice !== '' ? Number(editForm.basePrice) : undefined,
        roadQualityFactor: editForm.roadQualityFactor !== '' ? Number(editForm.roadQualityFactor) : undefined
      };

      await estimateAPI.updateTruckingParameter(editingId, data);
      setFeedback({ type: 'success', message: 'Parameter updated successfully' });
      setEditingId(null);
      setEditForm({});
      // Refresh the list
      await fetchParams();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to update parameter' });
      console.error('TruckingParameters save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Loading trucking parameters...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Trucking Parameters</h1>
      </div>

      {feedback && (
        <div className={feedback.type === 'success' ? 'success-banner' : 'error-message'}
          style={{ marginBottom: '1rem' }}>
          <p>{feedback.message}</p>
        </div>
      )}

      {params.length === 0 ? (
        <div className="empty-state">
          <p>No trucking parameters configured yet.</p>
        </div>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Region</th>
              <th>Direction</th>
              <th>Cost/km</th>
              <th>Base Price</th>
              <th>Road Factor</th>
              <th>Notes</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {params.map((param) => (
              <tr key={param.id}>
                {editingId === param.id ? (
                  <>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.region}
                        onChange={(e) => handleChange('region', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.direction}
                        onChange={(e) => handleChange('direction', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input admin-inline-narrow"
                        type="number"
                        step="0.01"
                        value={editForm.costPerKm}
                        onChange={(e) => handleChange('costPerKm', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input admin-inline-narrow"
                        type="number"
                        step="0.01"
                        value={editForm.basePrice}
                        onChange={(e) => handleChange('basePrice', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input admin-inline-narrow"
                        type="number"
                        step="0.01"
                        value={editForm.roadQualityFactor}
                        onChange={(e) => handleChange('roadQualityFactor', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="admin-inline-input"
                        type="text"
                        value={editForm.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                      />
                    </td>
                    <td>{formatDate(param.updatedAt)}</td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{param.region || '-'}</td>
                    <td>{param.direction || '-'}</td>
                    <td>{param.costPerKm != null ? `$${Number(param.costPerKm).toFixed(2)}` : '-'}</td>
                    <td>{param.basePrice != null ? `$${Number(param.basePrice).toFixed(2)}` : '-'}</td>
                    <td>{param.roadQualityFactor != null ? Number(param.roadQualityFactor).toFixed(2) : '-'}</td>
                    <td className="text-small">{param.notes || '-'}</td>
                    <td>{formatDate(param.updatedAt)}</td>
                    <td className="actions">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(param)}
                      >
                        Edit
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TruckingParameters;
